import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Reactions from '../components/Reactions';
import { apiFetch, API_BASE, getToken } from '../services/apiClient';
import PoDetailModal from './admin/PoDetailModal';
import { ExtraWorkTab } from './admin/AdminBidDetail';
import { DrillCardModal, NveCardModal, LoopsCardModal } from './admin/AdminWorkflows';
import ActivityPills from '../components/ActivityPills';
import { jobNumber, jobName } from '../utils/ui';
import FourQuestions, { EMPTY_ANSWERS, composeUpdate, hasAnswer, isSupervisorNow } from '../components/FourQuestions';

// Field (Zone 2) workflow types shown as pills on the job — office/contract excluded.
const FIELD_WF_META = {
  vertical_drilling:   { label: 'Vertical Drilling',   emoji: '🕳️' },
  horizontal_drilling: { label: 'Directional Drilling', emoji: '↔️' },
  loops:               { label: 'Loops',               emoji: '🔁' },
  nve:                 { label: 'NVE',                  emoji: '⚡' },
  usa_digs:            { label: 'USA Digs',             emoji: '🚧' },
};
const FIELD_WF_ORDER = ['vertical_drilling', 'horizontal_drilling', 'nve', 'loops', 'usa_digs'];

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
const fmt$ = n => n == null ? '' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Section({ title, count, open, onToggle, children }) {
  return (
    <div style={{ background: '#fff', border: '1.5px solid #ececec', borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
      <button onClick={onToggle} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{title}{count != null ? <span style={{ color: '#9ca3af', fontWeight: 600 }}> · {count}</span> : ''}</span>
        <span style={{ color: '#e87722', fontSize: 18, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>›</span>
      </button>
      {open && <div style={{ padding: '0 16px 16px' }}>{children}</div>}
    </div>
  );
}

export default function SupProject() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [punch, setPunch] = useState([]);
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState('updates');
  const [poMajorOnly, setPoMajorOnly] = useState(false);
  const [newUpdate, setNewUpdate] = useState('');
  // A field supervisor writes the day's update as four questions (Ryan, Oct 14 2026).
  const fourQ = isSupervisorNow();
  const [answers, setAnswers] = useState(EMPTY_ANSWERS);
  const [newPunch, setNewPunch] = useState('');
  const [editingPO, setEditingPO] = useState(null);

  const [creatingEW, setCreatingEW] = useState(false);
  const [wfRows, setWfRows] = useState([]);      // this job's workflows (from /workflows)
  // Plans (Ryan, Oct 21 2026): the job's plan files, and its Fieldwire project's plans and tasks.
  const [plans, setPlans] = useState([]);
  const [fwLink, setFwLink] = useState(null);
  useEffect(() => {
    apiFetch(`/projects/${id}/plans`).then(d => setPlans(Array.isArray(d) ? d : [])).catch(() => setPlans([]));
    apiFetch(`/time/fieldwire-link?project_id=${encodeURIComponent(id)}`).then(setFwLink).catch(() => setFwLink(null));
  }, [id]);
  const [drillCard, setDrillCard] = useState(null);
  const [nveCard, setNveCard] = useState(null);
  const [loopsCard, setLoopsCard] = useState(null);
  function reloadWorkflows() {
    apiFetch(`/workflows?project=${encodeURIComponent(id)}`).then(all => setWfRows((Array.isArray(all) ? all : []).filter(w => String(w.project_id) === String(id)))).catch(() => {});
  }
  function openWorkflow(w) {
    if (w.type === 'vertical_drilling' || w.type === 'horizontal_drilling') setDrillCard(w);
    else if (w.type === 'usa_digs') navigate(`/sup/usa-digs/${w.id}`);
    else if (w.type === 'nve') setNveCard(w);
    else if (w.type === 'loops') setLoopsCard(w);
    else navigate(`/sup/workflows/${w.type}`);
  }
  function reloadProject() { apiFetch(`/projects/${id}`).then(p => setProject(p)).catch(() => {}); }
  async function startExtraWork() {
    setCreatingEW(true);
    try {
      await apiFetch(`/projects/${id}/sub-jobs`, { method: 'POST', body: JSON.stringify({ type: 'extra_work', name: 'Extra Work', sortOrder: 99 }) });
      reloadProject();
    } catch (e) { window.alert(e?.message || 'Could not start Extra Work'); }
    setCreatingEW(false);
  }
  function reloadUpdates() { apiFetch(`/projects/${id}/daily-updates`).then(d => setUpdates(Array.isArray(d) ? d : [])).catch(() => {}); }
  function reloadPunch() { apiFetch(`/projects/${id}/punch-list`).then(d => setPunch(Array.isArray(d) ? d : [])).catch(() => {}); }
  function reloadPos() { apiFetch(`/projects/${id}/purchase-orders`).then(d => setPos(Array.isArray(d) ? d : [])).catch(() => {}); }

  useEffect(() => {
    Promise.all([
      apiFetch(`/projects/${id}`).catch(() => null),
      apiFetch(`/projects/${id}/daily-updates`).catch(() => []),
      apiFetch(`/projects/${id}/punch-list`).catch(() => []),
      apiFetch(`/projects/${id}/purchase-orders`).catch(() => []),
      apiFetch('/vendors').catch(() => []),
    ]).then(([p, u, pl, po, v]) => {
      setProject(p); setUpdates(u || []); setPunch(pl || []); setPos(po || []); setVendors(Array.isArray(v) ? v : []);
    }).finally(() => setLoading(false));
    reloadWorkflows();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function addUpdate() {
    const text = fourQ ? composeUpdate(answers) : newUpdate.trim();
    if (!text) return;
    try { await apiFetch(`/projects/${id}/daily-updates`, { method: 'POST', body: JSON.stringify({ notes: text }) }); setNewUpdate(''); setAnswers(EMPTY_ANSWERS); reloadUpdates(); }
    catch (e) { window.alert(e?.message || 'Could not post the update — try again'); }
  }
  async function addPunch() {
    if (!newPunch.trim()) return;
    try { await apiFetch(`/projects/${id}/punch-list`, { method: 'POST', body: JSON.stringify({ description: newPunch.trim() }) }); setNewPunch(''); reloadPunch(); }
    catch (e) { window.alert(e?.message || 'Could not add the item — try again'); }
  }
  async function togglePunch(item) {
    const status = item.status === 'done' ? 'open' : 'done';
    setPunch(list => list.map(x => x.id === item.id ? { ...x, status } : x));
    try { await apiFetch(`/projects/${id}/punch-list/${item.id}`, { method: 'PATCH', body: JSON.stringify({ status }) }); } catch { reloadPunch(); }
  }

  const inp = { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 16, color: '#111', background: '#fff' };
  const addBtn = { padding: '11px 16px', borderRadius: 10, border: 'none', background: '#e87722', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' };
  const subJobs = Array.isArray(project?.subJobs) ? project.subJobs : [];

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ background: '#111', color: '#fff', paddingTop: 'max(env(safe-area-inset-top), 14px)', paddingBottom: 14, paddingLeft: 16, paddingRight: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0, color: '#e87722', lineHeight: 1 }}>‹</button>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 17, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project ? `${jobNumber(project) ? jobNumber(project) + ' · ' : ''}${jobName(project) || 'Project'}` : 'Project'}</div>
          {project?.client_name && <div style={{ fontSize: 12, color: '#b5b5b5' }}>{project.client_name}</div>}
        </div>
      </div>

      <div style={{ padding: 12, maxWidth: 540, margin: '0 auto' }}>
        {loading ? <p style={{ color: '#9ca3af', textAlign: 'center', paddingTop: 30 }}>Loading…</p> : (
          <>
            <div style={{ marginBottom: 12 }}><ActivityPills pageName="Job" scope="job" scopeId={id} summaryLabel="Job Summary" linkJobs={false} /></div>
            <Section title="Daily Updates" count={updates.length} open={openSection === 'updates'} onToggle={() => setOpenSection(s => s === 'updates' ? '' : 'updates')}>
              {fourQ ? (
                <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <FourQuestions value={answers} onChange={setAnswers} />
                  <button onClick={addUpdate} disabled={!hasAnswer(answers)} style={{ ...addBtn, padding: '12px', opacity: hasAnswer(answers) ? 1 : 0.5 }}>Post today's update</button>
                </div>
              ) : (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input value={newUpdate} onChange={e => setNewUpdate(e.target.value)} placeholder="Add an update…" style={inp} />
                <button onClick={addUpdate} style={addBtn}>Add</button>
              </div>
              )}
              {updates.length === 0 ? <p style={{ color: '#9ca3af', fontSize: 13 }}>No updates yet.</p> : updates.map(u => (
                <div key={u.id} style={{ borderTop: '1px solid #f3f4f6', padding: '10px 0' }}>
                  <div style={{ fontSize: 14, color: '#111', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{u.notes}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{u.author_name || ''} · {fmtDate(u.created_at)}</div>
                  <Reactions type="update" id={u.id} reactions={u.reactions} />
                </div>
              ))}
            </Section>

            <Section title="Purchase Orders" count={pos.length} open={openSection === 'pos'} onToggle={() => setOpenSection(s => s === 'pos' ? '' : 'pos')}>
              {pos.length === 0 ? <p style={{ color: '#9ca3af', fontSize: 13 }}>No POs on this job.</p> : (() => {
                const majorCount = pos.filter(p => p.is_major).length;
                const shown = poMajorOnly ? pos.filter(p => p.is_major) : pos;
                return (
                  <>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      <button onClick={() => setPoMajorOnly(false)}
                        style={{ padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${!poMajorOnly ? '#92400e' : '#e5e7eb'}`, background: !poMajorOnly ? '#92400e' : '#fff', color: !poMajorOnly ? '#fff' : '#6b7280', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>All ({pos.length})</button>
                      <button onClick={() => setPoMajorOnly(true)}
                        style={{ padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${poMajorOnly ? '#b91c1c' : '#fca5a5'}`, background: poMajorOnly ? '#b91c1c' : '#fef2f2', color: poMajorOnly ? '#fff' : '#b91c1c', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>⭐ Major only ({majorCount})</button>
                    </div>
                    {shown.length === 0 ? <p style={{ color: '#9ca3af', fontSize: 13 }}>No Major POs on this job.</p> : shown.map(po => (
                      <button key={po.id} onClick={() => setEditingPO(po)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', borderTop: '1px solid #f3f4f6', padding: '12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>PO #{po.po_number}{!!po.is_major && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5', padding: '1px 5px', borderRadius: 5 }}>⭐ MAJOR</span>}</div>
                          <div style={{ fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{po.description || '—'}</div>
                          <div style={{ fontSize: 12, color: '#9ca3af' }}>{po.vendor || ''}{po.status ? ' · ' + po.status : ''}</div>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#111', whiteSpace: 'nowrap' }}>{fmt$(po.cost)}</div>
                      </button>
                    ))}
                  </>
                );
              })()}
            </Section>

            {(() => {
              const fieldWf = FIELD_WF_ORDER
                .map(t => wfRows.find(w => w.type === t))
                .filter(Boolean);
              if (fieldWf.length === 0) return null;
              // Folds like the other sections (Ryan, Oct 21 2026).
              return (
                <Section title="Work Flows" count={fieldWf.length} open={openSection === 'wf'} onToggle={() => setOpenSection(s => s === 'wf' ? '' : 'wf')}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {fieldWf.map(w => {
                      const m = FIELD_WF_META[w.type] || { label: w.name || w.type, emoji: '📁' };
                      const isDrill = w.type === 'vertical_drilling' || w.type === 'horizontal_drilling';
                      return (
                        <button key={w.id} onClick={() => openWorkflow(w)}
                          style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#f8fafc', border: '1.5px solid #e5e7eb', borderRadius: 999, padding: '10px 15px', fontSize: 14, fontWeight: 700, color: '#1e3a5f', cursor: 'pointer' }}>
                          <span style={{ fontSize: 16, lineHeight: 1 }}>{m.emoji}</span>
                          {w.name || m.label}
                          {w.status === 'closed' && <span style={{ fontSize: 11, fontWeight: 800, color: '#57534e', background: '#f5f5f4', padding: '1px 8px', borderRadius: 10 }}>Closed</span>}
                          {w.type === 'nve' && w.nve_count != null && (
                            <span style={{ fontSize: 11, fontWeight: 800, color: '#b45309', background: '#fffbeb', padding: '1px 8px', borderRadius: 10 }}>{w.nve_count}</span>
                          )}
                          {isDrill && w.hole_count > 0 && (
                            <span style={{ fontSize: 11, fontWeight: 800, color: '#1e3a5f', background: '#eff6ff', padding: '1px 8px', borderRadius: 10 }}>{w.holes_done}/{w.hole_count}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </Section>
              );
            })()}

            {/* Plans: the job's own plan files, and Fieldwire's plans and tasks for it. */}
            <Section title="Plans" count={plans.length || null} open={openSection === 'plans'} onToggle={() => setOpenSection(s => s === 'plans' ? '' : 'plans')}>
              {fwLink && fwLink.openUrl && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                  <a href={fwLink.openUrl} target="_blank" rel="noreferrer"
                    style={{ display: 'block', textAlign: 'center', padding: '13px 8px', borderRadius: 12, border: '2px solid #0f766e', background: '#f0fdfa', color: '#0f766e', fontSize: 14.5, fontWeight: 800, textDecoration: 'none' }}>📐 Plans in Fieldwire ↗</a>
                  <a href={fwLink.openUrl.replace(/\/plans$/, '/tasks')} target="_blank" rel="noreferrer"
                    style={{ display: 'block', textAlign: 'center', padding: '13px 8px', borderRadius: 12, border: '2px solid #1d4ed8', background: '#eff6ff', color: '#1d4ed8', fontSize: 14.5, fontWeight: 800, textDecoration: 'none' }}>✅ Tasks in Fieldwire ↗</a>
                </div>
              )}
              {plans.length > 0 ? plans.map(pl => (
                <a key={pl.id} href={API_BASE.replace('/api', '') + pl.url} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderTop: '1px solid #f3f4f6', color: '#1d4ed8', fontSize: 14.5, fontWeight: 700, textDecoration: 'none' }}>
                  <span style={{ fontSize: 18 }}>{/pdf/i.test(pl.mime_type || pl.original_name || '') ? '📄' : '🖼️'}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pl.original_name || 'Plan'}</span>
                  <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>{fmtDate(pl.created_at)}</span>
                </a>
              )) : (
                <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>{fwLink && fwLink.openUrl ? 'No plan files on the job itself — the drawings are in Fieldwire above.' : 'No plans on this job yet. The office adds them on the job page (and in Fieldwire).'}</p>
              )}
            </Section>

            {(() => {
              const ewSj = subJobs.find(sj => sj.type === 'extra_work');
              const ewCount = ewSj?.extra_work_count;
              return (
                <Section title="Extra Work" count={ewCount} open={openSection === 'ew'} onToggle={() => setOpenSection(s => s === 'ew' ? '' : 'ew')}>
                  {ewSj ? (
                    <ExtraWorkTab sjId={ewSj.id} projectId={id} project={project} isAdminZone={false} API_BASE={API_BASE} getToken={getToken} showToast={m => window.alert(m)} />
                  ) : (
                    <div style={{ padding: '4px 0' }}>
                      <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 10 }}>Log extra work (Labor, Material, Equipment) as a Change Order for the PM to review and send to the owner/GC.</p>
                      <button onClick={startExtraWork} disabled={creatingEW} style={{ ...addBtn, width: '100%' }}>{creatingEW ? 'Starting…' : '+ Start Extra Work'}</button>
                    </div>
                  )}
                </Section>
              );
            })()}

            <Section title="Punch List" count={punch.length} open={openSection === 'punch'} onToggle={() => setOpenSection(s => s === 'punch' ? '' : 'punch')}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input value={newPunch} onChange={e => setNewPunch(e.target.value)} placeholder="Add a punch item…" style={inp} />
                <button onClick={addPunch} style={addBtn}>Add</button>
              </div>
              {punch.length === 0 ? <p style={{ color: '#9ca3af', fontSize: 13 }}>No punch items.</p> : punch.map(it => {
                const done = it.status === 'done';
                return (
                  <button key={it.id} onClick={() => togglePunch(it)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', borderTop: '1px solid #f3f4f6', padding: '12px 0', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <span style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${done ? '#16a34a' : '#cbd5e1'}`, background: done ? '#16a34a' : '#fff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>{done ? '✓' : ''}</span>
                    <span style={{ fontSize: 14, color: done ? '#9ca3af' : '#111', textDecoration: done ? 'line-through' : 'none' }}>{it.description}</span>
                  </button>
                );
              })}
            </Section>
          </>
        )}
      </div>

      {drillCard && (
        <DrillCardModal card={drillCard} pmName={() => ''} onClose={() => setDrillCard(null)} onChanged={reloadWorkflows} />
      )}
      {nveCard && (
        <NveCardModal card={nveCard} pmName={() => ''} onClose={() => { setNveCard(null); reloadWorkflows(); }} />
      )}
      {loopsCard && (
        <LoopsCardModal card={loopsCard} pmName={() => ''} onClose={() => { setLoopsCard(null); reloadWorkflows(); }} />
      )}

      {editingPO && (
        <PoDetailModal po={editingPO} vendors={vendors}
          onClose={() => setEditingPO(null)}
          onSaved={() => { reloadPos(); }} />
      )}
    </div>
  );
}
